import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import type { SearchCompanyProfileResult } from '../../shared/services/companyProfileService/types';
import type { LocationSuggestion } from '../../shared/services/locationService/locationService';
import type {
  ConfirmedRoles,
  RoleTitle,
} from '../../shared/services/roleService/roleService';
import { ReviewPageLayout } from '../components/ReviewPageLayout/ReviewPageLayout';
import type { Rating } from '../components/StarSelector/StarSelector';
import {
  CompanyNameQuestion,
  type CompanyNameValue,
} from '../questions/AutoSuggestionQuestions/CompanyNameQuestion/CompanyNameQuestion';
import {
  JobTitleQuestion,
  type JobTitleValue,
} from '../questions/AutoSuggestionQuestions/JobTitleQuestions/JobTitleQuestion';
import { LocationQuestion } from '../questions/AutoSuggestionQuestions/LocationQuestion/LocationQuestion';
import { FinishQuestion } from '../questions/GroupedQuestions/WorkHistoryQuestion/FinishQuestion';
import { StartQuestion } from '../questions/GroupedQuestions/WorkHistoryQuestion/StartQuestion';
import { OverallRatingQuestion } from '../questions/RatingQuesions/OverallRatingQuestion';
import { RateYourSalaryQuestion } from '../questions/SegmentedButtonQuestions/RateYourSalaryQuestion';
import { RecommendationQuestion } from '../questions/SegmentedButtonQuestions/RecommendationQuestion';
import { OverallExperienceDetailsQuestion } from '../questions/SimpleQuestions/OverallExperienceDetailsQuestion';
import { SimpleTitleQuestion } from '../questions/SimpleQuestions/SimpleTitleQuestion';
import {
  type MonthPickerValue,
  type PageProps,
  type ReviewPayload,
  type SalaryRating,
} from '../type';
import { validationRules } from '../validation/validationRules';
import { testWorkHistoryRequired } from '../validation/validator/workHistoryRequired';
import translations from './.vocab';

export interface SimplifiedWriteAReviewPageProps extends PageProps {
  getRoleTitles: (query: string) => Promise<RoleTitle[]>;
  isSubmitFailed: boolean;
  isSubmitting: boolean;
  onUpdateRole: (role: ConfirmedRoles) => void;
  searchCompanyProfiles: (query: string) => Promise<SearchCompanyProfileResult>;
  searchLocations: (query: string) => Promise<LocationSuggestion[]>;
  hideCompanyNameSearch?: boolean;
}

export const SimplifiedWriteAReviewPage = ({
  review,
  pageNumber,
  hideCompanyNameSearch,
  setter,
  getRoleTitles,
  searchCompanyProfiles,
  searchLocations,
  onSubmit,
  onUpdateRole,
  onAlertClosed,
  isSubmitFailed,
  isSubmitting,
}: SimplifiedWriteAReviewPageProps) => {
  const { t } = useTranslations(translations);
  const config = useConfig();

  // const prefilled = Boolean(review.isWorkHistoryPrefilled);
  const { language, zone } = config;

  const fields = {
    companyName: review?.companyName,
    overallRating: review?.overallRating,
    jobTitleText: review?.jobTitleText,
    workHistoryFrom: review?.workHistoryFrom,
    workHistoryTo: review?.workHistoryTo,
    workHistory: {
      from: review?.workHistoryFrom,
      to: review?.workHistoryTo,
    },
    title: review?.title,
    isRecommended: review?.isRecommended,
    salaryRating: review?.salaryRating,
    fullLocation: review?.fullLocation,
    overallExperienceDetails: review?.overallExperienceDetails,
  };

  const {
    companyName,
    overallRating,
    jobTitleText,
    workHistoryFrom,
    workHistoryTo,
    workHistory,
    title,
    isRecommended,
    salaryRating,
    fullLocation,
    overallExperienceDetails,
  } = validationRules(language);

  const rules = {
    companyName,
    overallRating,
    jobTitleText,
    workHistoryFrom,
    ...(!review?.isStillWorkHere
      ? {
          workHistoryTo,
          workHistory,
        }
      : {}),
    title,
    isRecommended,
    salaryRating,
    fullLocation,
    overallExperienceDetails,
  } as Rules<typeof fields>;

  const {
    validate,
    validateWithErrors,
    getTone,
    getMessage,
    handleValueChangeWithValidation,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    onUpdateRole({
      id: review.roleId,
      ...(review.companyName
        ? {
            company: {
              text: review.companyName,
            },
          }
        : {}),
      title: {
        text: review.jobTitleText ?? '',
      },
      from: review.workHistoryFrom as MonthPickerValue,
      to: review.workHistoryTo as MonthPickerValue,
    });

    if (isValid && onSubmit) {
      onSubmit();
    }
  };

  const handleCompanyNameChange = (value: CompanyNameValue) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'companyName',
      previousValue: review,
    })(value.companyName);

    setter((prev) => ({
      ...prev,
      companyId: value.companyId,
      organisationId: value.organisationId,
      companyProfilePublished: value.published,
    }));
  };

  const handleOverallRatingChange = (value: Rating) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'overallRating',
      previousValue: review,
    })(value);

    // trackClickOverallPageSubSection({
    //   config,
    //   context: reviewFormContext,
    //   currentPageSubsection: 'overall_experience',
    // });
  };

  const onRecommendationChange = (value: boolean) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'isRecommended',
      previousValue: review,
    })(value);

    // trackClickYourExperienceSubSection({
    //   config,
    //   context: reviewFormContext,
    //   currentPageSubsection: 'recommend_to_a_friend',
    //   variant: variantValue,
    // });
  };

  const handleSalaryRatingChange = (value: SalaryRating) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'salaryRating',
      previousValue: review,
    })(value);

    // trackClickYourSalarySubSection({
    //   config,
    //   context: reviewFormContext,
    //   currentPageSubsection: 'salary_rating',
    // });
  };

  const handleStartQuestionChange = (value: MonthPickerValue) => {
    setter({
      ...review,
      workHistoryFrom: value,
      workHistory: {
        ...review.workHistory,
        from: value,
      },
    });

    const shouldTestWorkHistory =
      testWorkHistoryRequired(value) &&
      review?.workHistoryTo &&
      testWorkHistoryRequired(review?.workHistoryTo);

    if (!review.isStillWorkHere) {
      validate({
        workHistoryFrom: value,
        ...(shouldTestWorkHistory
          ? {
              workHistory: {
                from: value,
                to: review?.workHistoryTo,
              },
            }
          : {}),
      });
    }

    // trackClickYourRoleSubSection({
    //   currentPageSubsection: 'start_date',
    //   config,
    //   context: reviewFormContext,
    //   prefilled,
    // });
  };

  const handleFinishQuestionChange = (value: MonthPickerValue) => {
    setter({
      ...review,
      workHistoryTo: value,
      workHistory: {
        ...review.workHistory,
        to: value,
      },
    });

    const shouldTestWorkHistory =
      testWorkHistoryRequired(value) &&
      review?.workHistoryFrom &&
      testWorkHistoryRequired(review?.workHistoryFrom);

    if (!review.isStillWorkHere) {
      validate({
        workHistoryTo: value,
        ...(shouldTestWorkHistory
          ? {
              workHistory: {
                from: review?.workHistoryFrom,
                to: value,
              },
            }
          : {}),
      });
    }

    // trackClickYourRoleSubSection({
    //   currentPageSubsection: 'end_date',
    //   config,
    //   context: reviewFormContext,
    //   prefilled,
    // });
  };

  const handleIsStillWorkHereChange = () => {
    setter({ ...review, isStillWorkHere: !review.isStillWorkHere });
    // trackClickYourRoleSubSection({
    //   currentPageSubsection: 'i_still_work_here',
    //   config,
    //   context: reviewFormContext,
    //   prefilled,
    // });
  };

  const handleJobTitleQuestionChange = (value: JobTitleValue) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'jobTitleText',
      previousValue: review,
    })(value.text);

    setter((prev) => ({
      ...prev,
      jobTitleId: value.id,
    }));
  };

  const handleLocationChange = (value: LocationSuggestion) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'fullLocation',
      previousValue: review,
    })(value.text);

    setter((prev) => ({
      ...prev,
      locationId: value.id,
    }));
  };

  const onSearchCompanyProfiles = async (query: string) => {
    return searchCompanyProfiles(query);
  };

  return (
    <ReviewPageLayout
      progress={pageNumber}
      companyName={review?.companyName}
      subtitle={t(
        'All reviews are anonymous. We wont share your name, only details about your role.',
      )}
      logo={review?.branding?.logoImageUrl}
      onContinue={handleOnContinue}
      testid="simplified-write-a-review-page"
      hideStepper={true}
      onAlertClosed={isSubmitFailed ? onAlertClosed : undefined}
      isContinueLoading={isSubmitting}
      alertText={t(
        "We can't create your review right now. We're doing our best to fix this. Try refreshing the page or check back later.",
      )}
    >
      <Stack space="small">
        {!hideCompanyNameSearch && (
          <CompanyNameQuestion
            onChange={handleCompanyNameChange}
            searchCompanyProfiles={onSearchCompanyProfiles}
            text={review?.companyName}
            value={{
              companyId: review?.companyId,
              organisationId: review?.organisationId,
              companyName: review?.companyName,
            }}
            tone={getTone('companyName')}
            message={getMessage('companyName')}
            onValueSelected={() => {
              // trackClickOverallPageSubSection({
              //   config,
              //   context: reviewFormContext,
              //   currentPageSubsection: 'company_name',
              // });
            }}
          />
        )}

        <OverallRatingQuestion
          onChange={handleOverallRatingChange}
          value={review?.overallRating}
          message={getMessage('overallRating')}
        />

        <OverallExperienceDetailsQuestion
          onChange={handleValueChangeWithValidation({
            setter,
            validatorProperty: 'overallExperienceDetails',
            previousValue: review,
          })}
          value={review?.overallExperienceDetails}
          tone={getTone('overallExperienceDetails')}
          message={getMessage('overallExperienceDetails')}
        />

        <SimpleTitleQuestion
          onChange={handleValueChangeWithValidation({
            setter,
            validatorProperty: 'title',
            previousValue: review,
          })}
          value={review?.title}
          tone={getTone('title')}
          message={getMessage('title')}
        />

        <RecommendationQuestion
          onChange={onRecommendationChange}
          value={review?.isRecommended}
          tone={getTone('isRecommended')}
          message={getMessage('isRecommended')}
        />

        <RateYourSalaryQuestion
          onChange={handleSalaryRatingChange}
          tone={getTone('salaryRating')}
          message={getMessage('salaryRating')}
          value={review?.salaryRating}
        />

        <StartQuestion
          value={review?.workHistoryFrom}
          onChange={handleStartQuestionChange}
          tone={getTone('workHistoryFrom')}
          message={getMessage('workHistoryFrom')}
        />

        <FinishQuestion
          value={review?.workHistoryTo}
          onChange={handleFinishQuestionChange}
          isStillWorkHere={Boolean(review?.isStillWorkHere)}
          onIsStillWorkHereChange={handleIsStillWorkHereChange}
          tone={
            getTone('workHistoryTo') === 'critical'
              ? getTone('workHistoryTo')
              : getTone('workHistory')
          }
          message={getMessage('workHistoryTo') || getMessage('workHistory')}
        />

        <JobTitleQuestion
          onChange={handleJobTitleQuestionChange}
          value={{ text: review?.jobTitleText, id: review?.jobTitleId }}
          tone={getTone('jobTitleText')}
          message={getMessage('jobTitleText')}
          getRoleTitles={getRoleTitles}
          onValueSelected={() => {
            // trackClickYourRoleSubSection({
            //   currentPageSubsection: 'job_title',
            //   config,
            //   context: reviewFormContext,
            //   prefilled,
            // });
          }}
        />

        <LocationQuestion
          onChange={handleLocationChange}
          searchLocations={searchLocations}
          value={{
            text: review?.fullLocation,
            id: review?.locationId,
          }}
          zone={zone}
          tone={getTone('fullLocation')}
          message={getMessage('fullLocation')}
          onValueSelected={() => {
            // trackClickYourRoleSubSection({
            //   currentPageSubsection: 'location_selected',
            //   config,
            //   context: reviewFormContext,
            //   prefilled,
            // });
          }}
        />
      </Stack>
    </ReviewPageLayout>
  );
};
